import { Component, Inject, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Auth,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  signInWithPopup,
  GoogleAuthProvider,
} from '@angular/fire/auth';
import {
  doc,
  Firestore,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from '@angular/fire/firestore';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { RegisterComponent } from '../register/register.component';
import { NotRegisteredDialogComponent } from './not-registered-dialog/not-registered-dialog.component';
import { ForgotPasswordDialogComponent } from '../forgot-password-dialog/forgot-password-dialog.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  activeTab: 'email' | 'mobile' = 'mobile';

  countryList = [
    { name: 'UAE', code: '+971', flag: 'assets/flags/uae.png' },
    { name: 'India', code: '+91', flag: 'assets/flags/india.png' },
    { name: 'Saudi Arabia', code: '+966', flag: 'assets/flags/saudi.png' },
    { name: 'Kuwait', code: '+965', flag: 'assets/flags/kuwait.png' },
    { name: 'Oman', code: '+968', flag: 'assets/flags/oman.png' },
    { name: 'Qatar', code: '+974', flag: 'assets/flags/qatar.png' },
  ];

  user: any = {
    email: '',
    password: '',
    phoneNumber: '',
    countryCode: '+971',
    mobile: '',
  };
  otp: string = '';
  otpSent = false;
  confirmationResult!: ConfirmationResult;
  recaptchaVerifier!: RecaptchaVerifier;
  isSubmitting = false;
  submitted = false;

  /* ------------------------------- */
  /*  DESIGN LOGIC (From Register)   */
  /* ------------------------------- */
  otpDigits: string[] = ['', '', '', '', '', ''];
  hidePassword = true;

  /* Helper for Template */
  getSelectedCountry() {
    return this.countryList.find((c) => c.code === this.user.countryCode);
  }

  openForgotPasswordDialog() {
    this.dialog.open(ForgotPasswordDialogComponent, {
      width: '400px',
      disableClose: false,
    });
  }

  /* Back Button Logic */
  handleBack() {
    if (this.otpSent && !this.confirmationResult) {
      this.otpSent = false;
      this.otpDigits = ['', '', '', '', '', ''];
      this.otp = '';
    } else if (this.otpSent) {
      this.otpSent = false;
      this.otpDigits = ['', '', '', '', '', ''];
      this.otp = '';
    } else {
      // Navigate Back logic. 
      // We don't want to go TO the returnUrl (that's for after success).
      // If we are coming from a checkout flow, we go back to Cart.
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '';

      if (returnUrl.includes('confirm-enquiry') || returnUrl.includes('cart')) {
        this.router.navigate(['/cart']);
      } else {
        this.router.navigate(['/home']);
      }
    }
  }

  /* OTP Input Focus Logic */
  moveFocus(event: any, index: number) {
    const input = event.target;
    let val = input.value.replace(/[^0-9]/g, '');

    // Handle Paste / Autofill
    if (val.length > 1) {
      for (let i = 0; i < val.length && index + i < 6; i++) {
        this.otpDigits[index + i] = val[i];
      }
      this.otp = this.otpDigits.join('');

      const nextIndex = Math.min(index + val.length, 5);
      setTimeout(() => {
        const nextInput = document.getElementById(`otp-${nextIndex}`);
        nextInput?.focus();
      }, 0);
      return;
    }

    // Keep only the last character typed (prevents duplication)
    const singleVal = val.slice(-1);
    input.value = singleVal;
    this.otpDigits[index] = singleVal;

    if (singleVal && index < 5) {
      setTimeout(() => {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }, 0);
    }

    // Update main OTP string
    this.otp = this.otpDigits.join('');
  }

  switchTab(tab: 'email' | 'mobile') {
    this.activeTab = tab;
    this.submitted = false;
  }

  trackByIdx(index: number): any {
    return index;
  }

  handleBackspace(event: any, index: number) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.otpDigits[index]) {
        // Clear current field
        this.otpDigits[index] = '';
        event.target.value = '';
      } else if (index > 0) {
        // Move to previous field and clear it
        this.otpDigits[index - 1] = '';
        const prevInput = document.getElementById(
          `otp-${index - 1}`,
        ) as HTMLInputElement;
        if (prevInput) {
          prevInput.value = '';
          prevInput.focus();
        }
      }
      this.otp = this.otpDigits.join('');
    }
  }

  /* Apple Signup Placeholder */
  appleLogin() {
    this.uxservice.showError('Apple Sign-In is not yet configured.');
  }

  constructor(
    private auth: Auth,
    private afs: Firestore,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private uxservice: UxserviceService,
  ) { }

  ngOnInit() {
    // No recaptcha initialization here anymore
  }

  /** ✅ Initialize reCAPTCHA - Page View */
  ngAfterViewInit() {
    try {
      this.recaptchaVerifier = new RecaptchaVerifier(
        'login-recaptcha-container',
        { size: 'invisible' },
        this.auth,
      );
      this.recaptchaVerifier.render().catch(e => console.warn('Recaptcha render error', e));
    } catch (error) {
      console.warn('Recaptcha initialization failed (likely rerender):', error);
    }
  }

  ngOnDestroy() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
    }
  }

  /** 🔹 Email Login */
  async loginHandler(form: NgForm, e: Event) {
    e.preventDefault();
    this.submitted = true;
    if (form.invalid) {
      Object.keys(form.controls).forEach((key) =>
        form.controls[key]?.markAsTouched(),
      );
      return;
    }

    if (this.activeTab === 'email') {
      this.isSubmitting = true;
      this.uxservice.showOverlay('Logging in...');
      try {
        const credential = await signInWithEmailAndPassword(
          this.auth,
          this.user.email.toLowerCase(),
          this.user.password,
        );
        await this.updateUserCollection(credential.user.uid);
      } catch (error: any) {
        this.uxservice.instantCloseOverlay();
        this.uxservice.showError('Authentication Failed: ' + error.message);
        this.isSubmitting = false;
      }
    }
  }

  /** 🔹 Google Login */
  async googleLogin() {
    this.isSubmitting = true;
    this.uxservice.showOverlay('Connecting to Google...');
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(this.auth, provider);
      await this.handleGoogleUser(credential.user);
    } catch (error: any) {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError(error.message);
      this.isSubmitting = false;
    }
  }

  /** 🔹 Handle Google User (Create if new, then login) */
  private async handleGoogleUser(user: any) {
    const userRef = doc(this.afs, 'users-list', user.uid);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      // Create new normaluser record — role will be selected on homepage
      let firstName = user.displayName?.split(' ')[0] || 'User';
      firstName = firstName.charAt(0).toUpperCase() + firstName.slice(1);

      const userData = {
        uid: user.uid,
        firstname: firstName,
        displayname: firstName,
        fullname: user.displayName,
        email: user.email.toLowerCase(),
        phoneNumber: '',
        role: 'customer',     // Social login also defaults to customer
        isIndividual: true,   // Individual by default
        isVisiting: false,    // No longer using visiting state
        createdAt: new Date().toISOString(),
        profilePicUrl: user.photoURL || '',
      };
      await setDoc(userRef, userData, { merge: true });
    }

    // Proceed to login
    await this.updateUserCollection(user.uid);
  }

  /** 🔹 Step 1: Send OTP — first check if user is registered */
  async sendOtp() {
    this.submitted = true;
    if (!this.user.mobile) {
      this.uxservice.showError('Please enter a valid mobile number');
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Checking account...');

    try {
      const sanitizedMobile = this.user.mobile.replace(/\D/g, '');
      const phoneNumber = `${this.user.countryCode}${sanitizedMobile}`;

      // Check if user exists in users-list before sending OTP
      const usersRef = collection(this.afs, 'users-list');
      const userQuery = query(
        usersRef,
        where('phoneNumber', '==', phoneNumber),
      );
      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.empty) {
        // User not registered — show registration popup
        this.uxservice.instantCloseOverlay();
        this.isSubmitting = false;
        this.showNotRegisteredDialog();
        return;
      }

      // User exists — proceed to send OTP
      if (!this.recaptchaVerifier) {
        this.recaptchaVerifier = new RecaptchaVerifier(
          'login-recaptcha-container',
          { size: 'invisible' },
          this.auth,
        );
      }

      this.confirmationResult = await signInWithPhoneNumber(
        this.auth,
        phoneNumber,
        this.recaptchaVerifier,
      );

      this.otpSent = true;
      this.uxservice.instantCloseOverlay();
      this.uxservice.showSuccess('OTP sent successfully');
    } catch (err: any) {
      this.uxservice.instantCloseOverlay();
      if (err.code === 'auth/too-many-requests') {
        this.uxservice.showError(
          'Too many OTP attempts. Please wait a while or use a different number.',
        );
      } else {
        this.uxservice.showError('Failed to send OTP: ' + err.message);
      }
      this.isSubmitting = false;
    }
  }

  /** Show "Not Registered" dialog with register options */
  private showNotRegisteredDialog() {
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];

    this.dialog
      .open(NotRegisteredDialogComponent, {
        width: '420px',
        panelClass: 'not-registered-dialog',
      })
      .afterClosed()
      .subscribe((result: string) => {
        const queryParams = returnUrl ? { returnUrl } : {};

        if (result === 'vendor') {
          this.router.navigate(['/register-business'], { queryParams });
        } else if (result === 'customer') {
          this.router.navigate(['/register'], { queryParams });
        }
      });
  }

  /** 🔹 Step 2: Verify OTP */
  async verifyOtp() {
    /* Combine digits if using array */
    this.otp = this.otpDigits.join('');

    if (!this.otp || this.otp.length !== 6) {
      this.uxservice.showError('Please enter the 6-digit OTP');
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Verifying OTP...');

    try {
      const result = await this.confirmationResult.confirm(this.otp);
      await this.updateUserCollection(result.user.uid);
    } catch (err: any) {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('Invalid OTP: ' + err.message);
      this.isSubmitting = false;

      // Clear OTP and Focus first digit
      this.otpDigits = ['', '', '', '', '', ''];
      this.otp = '';
      setTimeout(() => {
        const firstInput = document.getElementById('otp-0');
        firstInput?.focus();
      }, 100);
    }
  }

  /** 🔹 Update user doc + navigate based on role */
  private async updateUserCollection(uid: string) {
    const userRef = doc(this.afs, 'users-list', uid);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      // 🚫 User authenticated via Firebase but NOT in our DB
      await this.auth.signOut(); // Force sign-out
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('User record not found. Please register first.');
      this.isSubmitting = false;
      this.otpSent = false;
      return;
    }

    const userData = docSnap.data();

    // 🚫 Reject soft-deleted workers
    if (userData?.['isdeleted'] === true) {
      await this.auth.signOut();
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('This account has been deactivated. Please contact your company administrator.');
      this.isSubmitting = false;
      this.otpSent = false;
      return;
    }

    await setDoc(
      userRef,
      { lastLoginAt: new Date().toISOString() },
      { merge: true },
    );

    this.uxservice.instantCloseOverlay();

    // Route based on role
    if (userData?.['role'] && userData['role'] !== 'customer' && userData['role'] !== 'normaluser') {
      // 🚫 Check if vendor account or worker parent account is disabled
      const isVendorDisabled = (userData['role'] === 'business' && userData['accountStatus'] === 'disabled');
      let isWorkerDisabled = false;
      if (userData['role'] === 'worker' && userData['parentUid']) {
        try {
          const parentSnap = await getDoc(doc(this.afs, 'users-list', userData['parentUid']));
          if (parentSnap.exists() && parentSnap.data()?.['accountStatus'] === 'disabled') {
            isWorkerDisabled = true;
          }
        } catch (e) {
          console.error('Error checking parent vendor status on login:', e);
        }
      }

      if (isVendorDisabled || isWorkerDisabled) {
        // Disabled vendor/worker stays authenticated and is routed to homepage.
        // Clicking "Vendor Dashboard" in the header will display the account disabled message.
        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
        this.router.navigateByUrl(returnUrl.startsWith('/business') ? '/home' : returnUrl);
        this.isSubmitting = false;
        return;
      }

      switch (userData['role']) {
        case 'business':
          this.router.navigate(['/business']);
          break;

        case 'worker':
          this.router.navigate(['/business']);
          break;

        case 'servicemanager':
          this.router.navigate(['/servicemanager']);
          break;
        case 'productmanager':
          this.router.navigate(['/productmanager']);
          break;
        case 'admin':
          this.router.navigate(['/admin']);
          break;
      }
    } else {
      // ✅ Customer / normaluser Role Logic — go to homepage
      // normaluser will see role-selection dialog on the landing page
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
      this.router.navigateByUrl(returnUrl);
    }

    this.isSubmitting = false;
  }

  /** 🔹 Open Register modal */
  openCreatemyaccountbox() {
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    const queryParams = returnUrl ? { returnUrl } : {};
    this.router.navigate(['/register'], { queryParams });
  }
}
